import axios from "axios";
import { useEffect, useState, useRef, memo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import {
  FaSave,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { MdOutlinePendingActions } from "react-icons/md";
import backarrow from "../assets/icon/arrow-right.svg";
import {
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_URL;

const icons = [
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
];

const staticBackgroundIcons = (() => {
  const arr = [];
  for (let i = 0; i < 5; i++)
    for (let j = 0; j < 11; j++) {
      const Icon = icons[Math.floor(Math.random() * icons.length)];
      arr.push({
        Icon,
        size: 20 + Math.random() * 25,
        top: (i / 5) * 100 + Math.random() * 5,
        left: (j / 11) * 100 + Math.random() * 5,
        duration: 12 + Math.random() * 10,
        opacity: 0.15 + Math.random() * 0.25,
        xOffset: Math.random() * 10 - 5,
        yOffset: Math.random() * 10 - 5,
      });
    }
  return arr;
})();

const AnimatedBackground = memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {staticBackgroundIcons.map((item, i) => (
      <motion.div
        key={i}
        className="absolute text-white/50"
        style={{
          top: `${item.top}%`,
          left: `${item.left}%`,
          opacity: item.opacity,
        }}
        initial={{ scale: 0.5 }}
        animate={{
          x: [0, item.xOffset, 0],
          y: [0, item.yOffset, 0],
          scale: [0.5, 1, 0.5],
        }}
        transition={{
          duration: item.duration,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <item.Icon size={item.size} />
      </motion.div>
    ))}
  </div>
));

const StudentPayments = () => {
  const [course, setCourse] = useState(null);
  const [payment, setPayment] = useState(null);
  const [amountPaidInput, setAmountPaidInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { courseid, studentid } = location.state || {};
  const inputRef = useRef(null);

  useEffect(() => {
    if (!courseid || !studentid) navigate("/Mainpage/StudentCourses");
  }, [courseid, studentid, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!courseid || !studentid) {
        setLoading(false);
        return;
      }
      try {
        const [cRes, pRes] = await Promise.all([
          axios.get(
            `${API_BASE}/api/course/Get-Single-Course-By-Id/${courseid}`,
            { headers: { Authorization: localStorage.getItem("token") } }
          ),
          axios.get(
            `${API_BASE}/api/student/Student-Payment-Get/${studentid}/${courseid}`,
            { headers: { Authorization: localStorage.getItem("token") } }
          ),
        ]);
        const cData = cRes.data?.data?.Courseinf;
        setCourse(cData);

        let pData = pRes.data?.data;
        if (pData) {
          if (pData.Totalamount === 0) pData.PaymentStatus = "Free";
          else if (pData.Amountpaid >= pData.Totalamount)
            pData.PaymentStatus = "FullyPaid";
          else pData.PaymentStatus = "PartiallyPaid";
          setPayment(pData);
          setAmountPaidInput(
            pData.Amountpaid > 0 ? pData.Amountpaid.toLocaleString("fa-IR") : ""
          );
        }
      } catch (e) {
        toast.error(
          e.response?.data?.message || "خطا در دریافت اطلاعات پرداخت."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseid, studentid]);

  const handleAmountPaidChange = (e) => {
    let input = e.target.value.replace(/[^\d]/g, "");
    setAmountPaidInput(input);
    const amountPaid = parseInt(input || "0", 10);
    if (!payment) return;
    if (amountPaid < 0) setError("مبلغ پرداختی نمی‌تواند منفی باشد!");
    else if (amountPaid > payment.Totalamount)
      setError("مبلغ پرداختی نمی‌تواند از مبلغ نهایی بیشتر باشد!");
    else setError("");
    setPayment((prev) => ({
      ...prev,
      Amountpaid: amountPaid,
      Unpaidamount: Math.max(0, prev.Totalamount - amountPaid),
      PaymentStatus:
        prev.Totalamount === 0
          ? "Free"
          : amountPaid >= prev.Totalamount
          ? "FullyPaid"
          : "PartiallyPaid",
    }));
  };

  const handleBlur = () => {
    setAmountPaidInput(
      amountPaidInput && parseInt(amountPaidInput.replace(/,/g, ""), 10) > 0
        ? parseInt(amountPaidInput.replace(/,/g, ""), 10).toLocaleString(
            "fa-IR"
          )
        : ""
    );
  };
  const handleFocus = () => {
    if (payment)
      setAmountPaidInput(
        payment.Amountpaid ? payment.Amountpaid.toString() : ""
      );
  };

  const handleSubmit = async () => {
    if (!payment || error) return;
    setSubmitting(true);
    try {
      const res = await axios.put(
        `${API_BASE}/api/student/Student-Payment-Update/${studentid}/${courseid}`,
        { Amountpaid: payment.Amountpaid },
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      if (res.status === 200) {
        toast.success("اطلاعات پرداخت با موفقیت ذخیره شد.");
        navigate("/Mainpage/StudentCourses");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "خطا در ذخیره اطلاعات پرداخت.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="w-12 h-12 border-4 border-t-white border-gray-600 rounded-full"
        />
        <p className="text-white text-lg font-bold mr-4">
          در حال دریافت اطلاعات...
        </p>
      </div>
    );
  if (!course || !payment) return <Navigate to="/Mainpage/StudentCourses" />;
  if (!localStorage.getItem("token")) return <Navigate to="/" />;

  return (
    <div className="relative min-h-screen w-full bg-gray-800 flex items-center justify-center p-3">
      <AnimatedBackground />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-5 w-full max-w-lg border border-white/30"
      >
        <div className="flex items-center justify-between mb-3">
          <img
            src={backarrow}
            alt="back"
            className="w-7 h-7 cursor-pointer"
            onClick={() =>
              navigate("/Mainpage/StudentCourses", { state: { studentid } })
            }
          />
          <h2 className="text-lg font-bold text-gray-800">
            مدیریت پرداخت دوره
          </h2>
          <div className="w-7" />
        </div>
        <div className="text-center mb-3">
          <p className="text-md font-semibold text-gray-700">
            {course.CourseName}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white/70 rounded-xl shadow p-3 text-center"
          >
            <FaMoneyBillWave className="text-gray-800 text-3xl mx-auto mb-2" />
            <p className="text-gray-600 text-sm">مبلغ نهایی</p>
            <p className="text-lg font-bold text-gray-800 mt-1">
              {payment.Totalamount.toLocaleString("fa-IR")} تومان
            </p>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white/70 rounded-xl shadow p-3 text-center"
          >
            <FaMoneyBillWave className="text-yellow-600 text-3xl mx-auto mb-2" />
            <p className="text-gray-600 text-sm">باقی‌مانده</p>
            <p className="text-lg font-bold text-gray-800 mt-1">
              {payment.Unpaidamount.toLocaleString("fa-IR")} تومان
            </p>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white/70 rounded-xl shadow p-4 text-center sm:col-span-2"
          >
            {payment.PaymentStatus === "FullyPaid" && (
              <FaCheckCircle className="text-green-600 text-3xl mx-auto mb-2" />
            )}
            {payment.PaymentStatus === "PartiallyPaid" && (
              <FaTimesCircle className="text-red-600 text-3xl mx-auto mb-2" />
            )}
            {payment.PaymentStatus === "Free" && (
              <MdOutlinePendingActions className="text-blue-600 text-3xl mx-auto mb-2" />
            )}
            <p className="text-gray-600 text-sm">وضعیت پرداخت</p>
            <p
              className={`text-lg font-bold mt-1 ${
                payment.PaymentStatus === "FullyPaid"
                  ? "text-green-600"
                  : payment.PaymentStatus === "PartiallyPaid"
                  ? "text-red-600"
                  : "text-blue-600"
              }`}
            >
              {payment.PaymentStatus === "FullyPaid"
                ? "پرداخت کامل"
                : payment.PaymentStatus === "PartiallyPaid"
                ? "پرداخت ناقص"
                : "رایگان"}
            </p>
          </motion.div>
        </div>

        {payment.Totalamount > 0 && (
          <>
            <div className="mb-3">
              <label className="block text-gray-800 font-bold text-sm mb-1">
                مبلغ پرداختی:
              </label>
              <input
                type="text"
                value={amountPaidInput}
                onChange={handleAmountPaidChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                ref={inputRef}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-400 focus:border-gray-700 outline-none text-gray-800 text-base bg-white/70"
                placeholder="مبلغ پرداختی را وارد کنید"
                dir="ltr"
              />
              {error && (
                <p className="text-red-600 text-sm mt-1 text-right">{error}</p>
              )}
            </div>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={submitting || !!error}
              className={`w-full py-3 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg text-base mt-2 ${
                submitting || error ? "opacity-80 cursor-not-allowed" : ""
              }`}
            >
              <FaSave className="ml-2 text-base inline" />
              {submitting ? "در حال ذخیره..." : "ذخیره اطلاعات پرداخت"}
            </motion.button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default StudentPayments;
